<template>
    <div class="p-4">
        <AccionesCabeceraPagina>
            <div class="d-flex gap-2">
                <template v-if="parte && !parte.cerrado">
                    <button class="btn btn-sm btn-success" :disabled="guardando" @click="guardar">
                        <span v-if="guardando" class="spinner-border spinner-border-sm me-md-1" />
                        <i v-else class="bi bi-check-lg me-md-1" />
                        <span class="d-none d-md-inline">Guardar</span>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" :disabled="guardando" @click="cerrarParte" title="Cerrar parte">
                        <i class="bi bi-lock me-md-1" />
                        <span class="d-none d-md-inline">Cerrar parte</span>
                    </button>
                </template>

                <RouterLink to="/mis-partes" class="btn btn-sm btn-outline-secondary">
                    <i class="bi bi-arrow-left me-md-1" />
                    <span class="d-none d-md-inline">Volver</span>
                </RouterLink>
            </div>
        </AccionesCabeceraPagina>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>

        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

        <template v-else-if="parte">
            <div class="row g-3">
                <!-- Info general -->
                <div class="col-12 col-lg-4">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Info general</span>
                        </div>
                        <div class="card-body">
                            <dl class="row mb-0">
                                <dt class="col-5 text-muted fw-normal small">Estado</dt>
                                <dd class="col-7 small">
                                    <span class="badge" :class="parte.cerrado ? 'bg-success' : 'bg-warning text-dark'">
                                        {{ parte.cerrado ? 'Cerrado' : 'Abierto' }}
                                    </span>
                                </dd>
                                <dt class="col-5 text-muted fw-normal small">Fecha</dt>
                                <dd class="col-7 small">{{ formatFecha(parte.fecha) }}</dd>
                                <dt class="col-5 text-muted fw-normal small">Tipo</dt>
                                <dd class="col-7 small">{{ parte.tipoParte?.descripcion ?? '—' }}</dd>
                                <dt class="col-5 text-muted fw-normal small">Cliente</dt>
                                <dd class="col-7 small">
                                    <RouterLink v-if="parte.cliente?.idc" :to="`/clientes/${parte.cliente.idc}`">
                                        {{ parte.cliente.nombreFiscal ?? parte.cliente.idc }}
                                    </RouterLink>
                                    <span v-else>—</span>
                                </dd>
                                <dt class="col-5 text-muted fw-normal small">Aviso</dt>
                                <dd class="col-7 small">
                                    <RouterLink v-if="parte.aviso?.idAviso" :to="`/mis-avisos/${parte.aviso.idAviso}`">
                                        #{{ parte.aviso.idAviso }}
                                    </RouterLink>
                                    <span v-else>—</span>
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                <!-- Formulario edición (solo si abierto) -->
                <div v-if="!parte.cerrado" class="col-12 col-lg-8">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Editar</span>
                        </div>
                        <div class="card-body">
                            <div v-if="errorGuardar" class="alert alert-danger py-2 small">{{ errorGuardar }}</div>
                            <div class="row g-3">
                                <div class="col-12 col-md-4">
                                    <label class="form-label small mb-1">Fecha</label>
                                    <input v-model="form.fecha" type="date" class="form-control form-control-sm" />
                                </div>
                                <div class="col-12 col-md-4">
                                    <label class="form-label small mb-1">Tipo parte</label>
                                    <AuxiliarSelect v-model="form.tipoParte" id-tipo-auxiliar="IdTipoParte" />
                                </div>
                                <div class="col-12">
                                    <label class="form-label small mb-1">Observaciones</label>
                                    <textarea v-model="form.observaciones" class="form-control form-control-sm" rows="3" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Observaciones (solo lectura si cerrado) -->
                <div v-if="parte.cerrado && parte.observaciones" class="col-12">
                    <div class="card">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Observaciones</span>
                        </div>
                        <div class="card-body">
                            <p class="small mb-0" style="white-space: pre-wrap;">{{ parte.observaciones }}</p>
                        </div>
                    </div>
                </div>

                <!-- Tramos -->
                <div class="col-12">
                    <div class="card">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Tramos</span>
                        </div>
                        <div class="card-body p-0">
                            <div v-if="errorTramo" class="alert alert-danger py-2 small m-2 mb-0">{{ errorTramo }}</div>
                            <div v-if="parte.tramos.length === 0 && parte.cerrado" class="text-center text-muted py-3 small">
                                Sin tramos.
                            </div>

                            <div class="d-md-none">
                                <div
                                    v-for="tramo in parte.tramos"
                                    :key="tramo.idParteTramo"
                                    class="border-bottom px-3 py-2"
                                >
                                    <div class="d-flex justify-content-between align-items-start gap-2 mb-1">
                                        <span class="small fw-semibold">{{ tramo.tipoTramo?.descripcion ?? '—' }}</span>
                                        <span class="small text-muted">{{ tramo.horas }}</span>
                                    </div>
                                    <div class="small text-muted">
                                        {{ tramo.inicio ? tramo.inicio.substring(11, 16) : '—' }} - {{ tramo.final ? tramo.final.substring(11, 16) : '—' }}
                                    </div>
                                    <div v-if="!parte.cerrado" class="mt-2 d-flex justify-content-end">
                                        <button class="btn btn-sm btn-outline-danger" :disabled="eliminandoTramo === tramo.idParteTramo" @click="eliminarTramo(tramo.idParteTramo)">
                                            <span v-if="eliminandoTramo === tramo.idParteTramo" class="spinner-border spinner-border-sm me-1" />
                                            <template v-else>
                                                <i class="bi bi-trash me-1" />Eliminar
                                            </template>
                                        </button>
                                    </div>
                                </div>

                                <div v-if="!parte.cerrado" class="px-3 py-3">
                                    <div class="row g-2">
                                        <div class="col-12">
                                            <label class="form-label small mb-1">Tipo</label>
                                            <AuxiliarSelect v-model="nuevoTramo.tipoTramo" id-tipo-auxiliar="IdTipoTramo" placeholder="Tipo..." />
                                        </div>
                                        <div class="col-12 col-sm-6">
                                            <label class="form-label small mb-1">Inicio</label>
                                            <input v-model="nuevoTramo.inicio" type="time" class="form-control form-control-sm" />
                                        </div>
                                        <div class="col-12 col-sm-6">
                                            <label class="form-label small mb-1">Final</label>
                                            <input v-model="nuevoTramo.final" type="time" class="form-control form-control-sm" />
                                        </div>
                                        <div class="col-12 d-flex justify-content-end">
                                            <button class="btn btn-sm btn-primary" :disabled="agregandoTramo || !nuevoTramo.tipoTramo" @click="agregarTramo">
                                                <span v-if="agregandoTramo" class="spinner-border spinner-border-sm me-1" />
                                                <i v-else class="bi bi-plus-lg me-1" />Añadir
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="d-none d-md-block table-responsive">
                                <table class="table table-sm mb-0">
                                    <thead class="table-light">
                                        <tr>
                                            <th class="ps-3">Tipo</th>
                                            <th>Inicio</th>
                                            <th>Final</th>
                                            <th>Horas</th>
                                            <th v-if="!parte.cerrado" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="tramo in parte.tramos" :key="tramo.idParteTramo">
                                            <td class="ps-3 small">{{ tramo.tipoTramo?.descripcion ?? '—' }}</td>
                                            <td class="small">{{ tramo.inicio ? tramo.inicio.substring(11, 16) : '—' }}</td>
                                            <td class="small">{{ tramo.final ? tramo.final.substring(11, 16) : '—' }}</td>
                                            <td class="small">{{ tramo.horas }}</td>
                                            <td v-if="!parte.cerrado" class="text-end pe-2">
                                                <button class="btn btn-sm btn-outline-danger" :disabled="eliminandoTramo === tramo.idParteTramo" @click="eliminarTramo(tramo.idParteTramo)">
                                                    <span v-if="eliminandoTramo === tramo.idParteTramo" class="spinner-border spinner-border-sm me-1" />
                                                    <template v-else>
                                                        <i class="bi bi-trash me-1" />Eliminar
                                                    </template>
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-if="!parte.cerrado">
                                            <td class="ps-3" style="min-width: 160px;">
                                                <AuxiliarSelect v-model="nuevoTramo.tipoTramo" id-tipo-auxiliar="IdTipoTramo" placeholder="Tipo..." />
                                            </td>
                                            <td>
                                                <input v-model="nuevoTramo.inicio" type="time" class="form-control form-control-sm" />
                                            </td>
                                            <td>
                                                <input v-model="nuevoTramo.final" type="time" class="form-control form-control-sm" />
                                            </td>
                                            <td />
                                            <td class="text-end pe-2">
                                                <button class="btn btn-sm btn-primary" :disabled="agregandoTramo || !nuevoTramo.tipoTramo" @click="agregarTramo">
                                                    <span v-if="agregandoTramo" class="spinner-border spinner-border-sm me-1" />
                                                    <i v-else class="bi bi-plus-lg me-1" />Añadir
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { misPartesApi } from '@/api/misPartesApi'
import { partesApi } from '@/api/partesApi'
import type { Parte } from '@/types/parte'
import type { Auxiliar } from '@/types/auxiliar'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import AuxiliarSelect from '@/components/AuxiliarSelect.vue'
import AccionesCabeceraPagina from '@/components/AccionesCabeceraPagina.vue'
import { useConfirm } from '@/composables/useConfirm'

const { confirm } = useConfirm()
const route = useRoute()
const idParte = Number(route.params.idParte)
const { title, mobileTitle } = usePageHeader(`#${idParte}`, `#${idParte}`)
const { crumbs } = useBreadcrumbs([{ label: 'Mis partes', to: '/mis-partes' }, { label: `#${idParte}` }])

const loading = ref(false)
const error = ref<string | null>(null)
const guardando = ref(false)
const errorGuardar = ref<string | null>(null)
const parte = ref<Parte | null>(null)

const errorTramo = ref<string | null>(null)
const eliminandoTramo = ref<number | null>(null)
const agregandoTramo = ref(false)
const nuevoTramo = ref<{ tipoTramo: Auxiliar | null; inicio: string; final: string }>({
    tipoTramo: null,
    inicio: '',
    final: '',
})

const form = ref({
    fecha: '',
    tipoParte: null as Auxiliar | null,
    observaciones: '',
})

function formatFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function cargarFormulario(p: Parte) {
    form.value = {
        fecha: p.fecha.substring(0, 10),
        tipoParte: p.tipoParte,
        observaciones: p.observaciones ?? '',
    }
}

async function guardar() {
    if (!parte.value) return
    guardando.value = true
    errorGuardar.value = null
    try {
        const updated = await misPartesApi.update(idParte, {
            fecha: form.value.fecha,
            idTipoParte: form.value.tipoParte?.idAuxiliar,
            observaciones: form.value.observaciones || undefined,
        })
        parte.value = updated
        cargarFormulario(updated)
    } catch {
        errorGuardar.value = 'No se pudo guardar.'
    } finally {
        guardando.value = false
    }
}

async function cerrarParte() {
    if (!parte.value) return
    if (!await confirm('¿Cerrar este parte? No podrá editarse después.', 'Cerrar parte')) return
    guardando.value = true
    errorGuardar.value = null
    try {
        const updated = await misPartesApi.update(idParte, { cerrado: 1 })
        parte.value = updated
    } catch {
        errorGuardar.value = 'No se pudo cerrar el parte.'
    } finally {
        guardando.value = false
    }
}

async function eliminarTramo(idParteTramo: number) {
    if (!parte.value) return
    if (!await confirm('¿Eliminar este tramo?', 'Eliminar tramo')) return
    eliminandoTramo.value = idParteTramo
    errorTramo.value = null
    try {
        await misPartesApi.deleteTramo(idParte, idParteTramo)
        parte.value = await partesApi.getById(idParte)
    } catch {
        errorTramo.value = 'No se pudo eliminar el tramo.'
    } finally {
        eliminandoTramo.value = null
    }
}

async function agregarTramo() {
    if (!parte.value || !nuevoTramo.value.tipoTramo) return
    agregandoTramo.value = true
    errorTramo.value = null
    try {
        const fecha = parte.value.fecha.substring(0, 10)
        await misPartesApi.createTramo(idParte, {
            tipoTramo: nuevoTramo.value.tipoTramo,
            ...(nuevoTramo.value.inicio ? { inicio: `${fecha} ${nuevoTramo.value.inicio}:00` } : {}),
            ...(nuevoTramo.value.final ? { final: `${fecha} ${nuevoTramo.value.final}:00` } : {}),
        })
        parte.value = await partesApi.getById(idParte)
        await cargarContextoTramo()
    } catch {
        errorTramo.value = 'No se pudo añadir el tramo.'
    } finally {
        agregandoTramo.value = false
    }
}

async function cargarContextoTramo() {
    try {
        const ctx = await misPartesApi.getTramoCreateContext(idParte)
        nuevoTramo.value = {
            tipoTramo: ctx.defaultTipoTramo ?? null,
            inicio: ctx.defaultInicio ? ctx.defaultInicio.substring(11, 16) : '',
            final: ctx.defaultFinal ? ctx.defaultFinal.substring(11, 16) : '',
        }
    } catch {
        // no bloqueante
    }
}

onMounted(async () => {
    loading.value = true
    error.value = null
    try {
        parte.value = await partesApi.getById(idParte)
        const label = parte.value?.cliente?.nombreFiscal
            ? `#${idParte} · ${parte.value.cliente.nombreFiscal}`
            : `#${idParte}`
        title.value = label
        mobileTitle.value = `#${idParte}`
        crumbs.value[1].label = label
        cargarFormulario(parte.value)
        if (!parte.value.cerrado) {
            cargarContextoTramo()
        }
    } catch {
        error.value = 'No se pudo cargar el parte.'
    } finally {
        loading.value = false
    }
})
</script>
